import * as React from 'react';
import { useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PageWrapper } from 'Views/Components/PageWrapper/PageWrapper';
import SecuredPage from 'Views/Components/Security/SecuredPage';
import { NewModal } from 'Views/Components/Modal/NewModal';
import Spinner from 'Views/Components/Spinner/Spinner';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { TextInput } from 'Views/Components/New/TextInput';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import {
	fetchTrappedCommunication,
	fetchTrappedCommunications,
	TrappedCommunicationDetail,
	TrappedCommunicationListItem,
	TrappedCommunicationStatus,
	TrappedCommunicationType,
} from 'Services/Api/_HumanWritten/TrappedCommunicationService';

const PER_PAGE = 20;

const TYPE_OPTIONS: { value: TrappedCommunicationType | ''; label: string }[] = [
	{ value: '', label: 'All' },
	{ value: 'Email', label: 'Email' },
	{ value: 'Sms', label: 'SMS' },
];

const STATUS_STYLES: Record<TrappedCommunicationStatus, string> = {
	Sent: 'bg-green-100 text-green-800',
	Failed: 'bg-red-100 text-red-800',
	Skipped: 'bg-yellow-100 text-yellow-800',
};

function StatusBadge({ status }: { status: TrappedCommunicationStatus }) {
	return (
		<span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}>
			{status}
		</span>
	);
}

/**
 * Renders the JSON content of a single trapped communication. Email bodies are HTML, so they are shown
 * inside a sandboxed iframe to isolate their styles/scripts from the admin app.
 */
function CommunicationContent({ detail }: { detail: TrappedCommunicationDetail }) {
	const parsed = useMemo<Record<string, unknown>>(() => {
		try {
			return JSON.parse(detail.content) as Record<string, unknown>;
		} catch {
			return {};
		}
	}, [detail.content]);

	if (detail.type === 'Email') {
		const body = typeof parsed.body === 'string' ? parsed.body : '';
		return (
			<div className="flex flex-col gap-3">
				<dl className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1 text-sm">
					<dt className="font-semibold text-gray-500">Subject</dt>
					<dd>{detail.subject ?? '—'}</dd>
					<dt className="font-semibold text-gray-500">To</dt>
					<dd className="break-words">{detail.recipient}</dd>
				</dl>
				<div className="rounded border border-gray-200">
					<iframe title="Email body" sandbox="" srcDoc={body} className="h-[70vh] w-full rounded bg-white" />
				</div>
			</div>
		);
	}

	const message = typeof parsed.message === 'string' ? parsed.message : detail.content;
	return (
		<div className="flex flex-col gap-2">
			<span className="text-sm font-semibold text-gray-500">Message</span>
			<pre className="whitespace-pre-wrap break-words rounded bg-gray-50 p-3 text-sm">{message}</pre>
		</div>
	);
}

function DetailModal({ id, onClose }: { id: string; onClose: () => void }) {
	const { data, isLoading, isError } = useQuery({
		queryKey: ['trappedCommunication', id],
		queryFn: () => fetchTrappedCommunication(id),
	});

	return (
		<NewModal isOpen label="Trapped communication" modalSize="xl" onRequestClose={onClose}>
			<NewModal.Header>
				<NewModal.Title>Communication details</NewModal.Title>
			</NewModal.Header>
			<NewModal.Body>
				{isLoading && <Spinner />}
				{isError && <p className="text-red-600">Failed to load communication.</p>}
				{data && (
					<div className="flex flex-col gap-4">
						<div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
							<span className="rounded bg-gray-100 px-2 py-0.5 font-medium">{data.type}</span>
							<StatusBadge status={data.status} />
							<span>{new Date(data.created).toLocaleString()}</span>
						</div>
						{data.error && (
							<MessageBox status="error" slim noBottomMargin>
								<span className="font-semibold">Detail: </span>
								{data.error}
							</MessageBox>
						)}
						<CommunicationContent detail={data} />
					</div>
				)}
			</NewModal.Body>
			<NewModal.Footer>
				<NewModal.CancelButton onClick={onClose}>Close</NewModal.CancelButton>
			</NewModal.Footer>
		</NewModal>
	);
}

const TrappedCommunicationsPage = observer(() => {
	const [searchInput, setSearchInput] = useState('');
	const [typeFilter, setTypeFilter] = useState<TrappedCommunicationType | ''>('');
	const [pageNo, setPageNo] = useState(0);
	const [selectedId, setSelectedId] = useState<string | null>(null);

	// Debounce the recipient search so we don't fire a request on every keystroke.
	const [debouncedSearch, setDebouncedSearch] = useState('');
	React.useEffect(() => {
		const handle = setTimeout(() => {
			setDebouncedSearch(searchInput);
			setPageNo(0);
		}, 250);
		return () => clearTimeout(handle);
	}, [searchInput]);

	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['trappedCommunications', debouncedSearch, typeFilter, pageNo],
		queryFn: () =>
			fetchTrappedCommunications({
				searchString: debouncedSearch,
				type: typeFilter || undefined,
				skip: PER_PAGE * pageNo,
				take: PER_PAGE,
			}),
		placeholderData: keepPreviousData,
		staleTime: 1000 * 30,
	});

	const rows: TrappedCommunicationListItem[] = data?.data ?? [];
	const total = data?.count ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

	const handleTypeChange = (opt: unknown) => {
		setTypeFilter((opt as { value: TrappedCommunicationType | '' } | null)?.value ?? '');
		setPageNo(0);
	};

	return (
		<SecuredPage groups={['Super Administrators', 'Admin']}>
			<PageWrapper>
				<div className="mx-auto max-w-6xl px-6 py-8">
					<header className="mb-8">
						<h1 className="text-2xl font-semibold text-gray-900">Trapped communications</h1>
						<p className="text-sm text-gray-500">Every email and SMS the system has attempted to send.</p>
					</header>

					<div className="rounded-xl border border-gray-200 bg-white shadow-sm">
						<div className="flex flex-wrap items-end gap-3 border-b border-gray-200 p-5">
							<div className="w-72">
								<TextInput
									label="Search recipient"
									value={searchInput}
									onChange={e => setSearchInput(e.target.value)}
									placeholder="Email or phone number"
								/>
							</div>
							<div className="w-48">
								<SelectInput
									label="Type"
									options={TYPE_OPTIONS}
									value={TYPE_OPTIONS.find(o => o.value === typeFilter) ?? null}
									onChange={handleTypeChange}
								/>
							</div>
							{isFetching && <Spinner />}
						</div>

						<div className="overflow-x-auto">
							<table className="w-full text-left text-sm">
								<thead className="bg-gray-50 text-xs uppercase text-gray-500">
									<tr>
										<th className="px-4 py-3">Date</th>
										<th className="px-4 py-3">Type</th>
										<th className="px-4 py-3">Recipient</th>
										<th className="px-4 py-3">Subject</th>
										<th className="px-4 py-3">Status</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-100">
									{isLoading && (
										<tr>
											<td colSpan={5} className="px-4 py-8 text-center">
												<Spinner />
											</td>
										</tr>
									)}
									{!isLoading && rows.length === 0 && (
										<tr>
											<td colSpan={5} className="px-4 py-8 text-center text-gray-400">
												No communications found.
											</td>
										</tr>
									)}
									{rows.map(row => (
										<tr
											key={row.id}
											onClick={() => setSelectedId(row.id)}
											className="cursor-pointer hover:bg-gray-50"
										>
											<td className="whitespace-nowrap px-4 py-3 text-gray-600">
												{new Date(row.created).toLocaleString()}
											</td>
											<td className="px-4 py-3">{row.type}</td>
											<td className="px-4 py-3 break-words">{row.recipient}</td>
											<td className="px-4 py-3 text-gray-600">{row.subject ?? '—'}</td>
											<td className="px-4 py-3">
												<StatusBadge status={row.status} />
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						<div
							className={
								'flex items-center justify-between border-t border-gray-200 ' +
								'p-5 text-sm text-gray-600'
							}
						>
							<span>{total} total</span>
							<div className="flex items-center gap-2">
								<button
									type="button"
									disabled={pageNo === 0}
									onClick={() => setPageNo(p => Math.max(0, p - 1))}
									className="rounded border border-gray-300 px-3 py-1 disabled:opacity-40"
								>
									Previous
								</button>
								<span>
									Page {pageNo + 1} of {totalPages}
								</span>
								<button
									type="button"
									disabled={pageNo + 1 >= totalPages}
									onClick={() => setPageNo(p => p + 1)}
									className="rounded border border-gray-300 px-3 py-1 disabled:opacity-40"
								>
									Next
								</button>
							</div>
						</div>
					</div>
				</div>

				{selectedId && <DetailModal id={selectedId} onClose={() => setSelectedId(null)} />}
			</PageWrapper>
		</SecuredPage>
	);
});

export default TrappedCommunicationsPage;
